import { useEffect, useState } from 'react';
import axios from 'axios';
import { Landmark, ChevronRight, IndianRupee } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtINR = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function PendingWithdrawalsCard() {
  const [data, setData] = useState({ summary: null, requests: [] });
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/pending-withdrawals`, {
        params: { limit: 50 }
      });
      setData(r.data || { summary: null, requests: [] });
    } catch (_) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const items = data.requests || [];
  const visible = showAll ? items : items.slice(0, 5);

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
      data-testid="dashboard-pending-withdrawals"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-100 text-cyan-700">
            <Landmark size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Withdraw Requests
            </p>
            <p className="text-xs text-gray-500">
              Delivery agents awaiting payout approval
            </p>
          </div>
        </div>

        <div className="text-right">
          <p className="text-[11px] text-gray-500 uppercase tracking-wide">
            Total Requested
          </p>
          <p
            className="text-xl font-bold text-cyan-700 flex items-center justify-end gap-0.5"
            data-testid="pending-withdrawals-total"
          >
            <IndianRupee size={16} />
            {Number(data.summary?.totalPending || 0).toLocaleString('en-IN', {
              maximumFractionDigits: 2
            })}
          </p>
          <p className="text-[11px] text-gray-500">
            across {data.summary?.count || 0} request
            {(data.summary?.count || 0) === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-sm text-gray-400 py-8">Loading…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No pending withdraw requests.</p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-gray-100" data-testid="pending-withdrawals-list">
            {visible.map((w) => (
              <Link
                key={w.id}
                to={`/delivery-agents?agentId=${w.agentId}&tab=withdrawals`}
                data-testid={`pending-withdrawal-row-${w.id}`}
                className="flex items-center justify-between gap-3 py-3 hover:bg-gray-50 -mx-2 px-2 rounded-md transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {w.agentImage ? (
                    <img
                      src={w.agentImage}
                      alt={w.agentName}
                      className="w-9 h-9 rounded-md object-cover bg-gray-100"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-md bg-gray-100 flex items-center justify-center text-gray-400 text-xs font-semibold">
                      {(w.agentName || '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {w.agentName || 'Unknown agent'}
                    </p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {w.agentPhone || '—'} · Requested {timeAgo(w.createdAt)}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0 flex items-center gap-1">
                  <span className="text-sm font-semibold text-cyan-700">
                    {fmtINR(w.amount)}
                  </span>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              </Link>
            ))}
          </div>

          {items.length > 5 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="pending-withdrawals-toggle"
              className="mt-3 text-xs font-medium text-cyan-700 hover:underline"
            >
              {showAll ? 'Show fewer' : `Show all ${items.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
